/* ============================================================
   GHAN BROS SEAFOOD — product page behaviour
   Three small custom elements, no dependencies beyond Dawn's
   <product-form> (which handles the AJAX add-to-cart itself):

     <ghan-gallery>  main image + thumbnail row
     <ghan-product>  option pills -> variant, price, qty, button
     <ghan-cutoff>   "order in the next 2 h 18 m" countdown

   Markup contract lives in sections/ghan-product.liquid.
   ============================================================ */

if (!customElements.get('ghan-gallery')) {
  customElements.define(
    'ghan-gallery',
    class GhanGallery extends HTMLElement {
      connectedCallback() {
        this.frames = Array.from(this.querySelectorAll('[data-media-id]'));
        this.thumbs = Array.from(this.querySelectorAll('[data-thumb-media-id]'));

        this.thumbs.forEach((thumb, index) => {
          thumb.addEventListener('click', () => this.select(thumb.dataset.thumbMediaId));
          thumb.addEventListener('keydown', (event) => this.onKeydown(event, index));
        });
      }

      onKeydown(event, index) {
        const step = event.key === 'ArrowRight' ? 1 : event.key === 'ArrowLeft' ? -1 : 0;
        if (step === 0) return;
        event.preventDefault();
        const next = this.thumbs[(index + step + this.thumbs.length) % this.thumbs.length];
        next.focus();
        this.select(next.dataset.thumbMediaId);
      }

      /* Public: called by <ghan-product> when the variant image changes. */
      select(mediaId) {
        if (!mediaId) return;
        const id = String(mediaId);
        let matched = false;

        this.frames.forEach((frame) => {
          const isActive = frame.dataset.mediaId === id;
          if (isActive) matched = true;
          frame.classList.toggle('is-active', isActive);
        });
        if (!matched) return;

        this.thumbs.forEach((thumb) => {
          thumb.setAttribute('aria-current', thumb.dataset.thumbMediaId === id ? 'true' : 'false');
        });
      }
    }
  );
}

if (!customElements.get('ghan-product')) {
  customElements.define(
    'ghan-product',
    class GhanProduct extends HTMLElement {
      connectedCallback() {
        const data = this.querySelector('[data-ghan-variants]');
        if (!data) return;

        try {
          this.variants = JSON.parse(data.textContent);
        } catch (error) {
          console.error('ghan-product: could not parse variant data', error);
          return;
        }

        this.currency = this.dataset.currency || 'AUD';
        this.locale = this.dataset.locale || 'en-AU';
        this.showPriceInButton = this.dataset.showPriceInButton === 'true';
        this.addLabel = this.dataset.addLabel || 'Add to cart';

        this.gallery = this.querySelector('ghan-gallery');
        this.priceTarget = this.querySelector('[data-ghan-price]');
        this.tradeTarget = this.querySelector('[data-ghan-trade]');
        this.skuTarget = this.querySelector('[data-ghan-sku]');
        this.idInput = this.querySelector('[data-ghan-variant-id]');
        this.submit = this.querySelector('[data-ghan-submit]');
        this.submitText = this.submit ? this.submit.querySelector('[data-ghan-submit-text]') : null;
        this.qtyInput = this.querySelector('[data-ghan-qty]');
        this.pickup = this.querySelector('pickup-availability');
        this.inputs = Array.from(this.querySelectorAll('[data-ghan-option]'));

        this.inputs.forEach((input) => input.addEventListener('change', () => this.onOptionChange()));
        this.bindQuantity();

        this.current = this.findVariant() || this.variants.find((variant) => variant.available) || this.variants[0];
        this.render({ userChange: false });
      }

      bindQuantity() {
        if (!this.qtyInput) return;
        const minus = this.querySelector('[data-ghan-qty-minus]');
        const plus = this.querySelector('[data-ghan-qty-plus]');

        if (minus) minus.addEventListener('click', () => this.stepQuantity(-1));
        if (plus) plus.addEventListener('click', () => this.stepQuantity(1));
        this.qtyInput.addEventListener('change', () => this.stepQuantity(0));
      }

      stepQuantity(delta) {
        const min = parseInt(this.qtyInput.min, 10) || 1;
        const step = parseInt(this.qtyInput.step, 10) || 1;
        const max = this.qtyInput.max ? parseInt(this.qtyInput.max, 10) : null;

        let value = (parseInt(this.qtyInput.value, 10) || min) + delta * step;
        if (value < min) value = min;
        if (max !== null && value > max) value = max;

        this.qtyInput.value = value;
        this.renderQuantityState();
        this.renderSubmit();
      }

      renderQuantityState() {
        const min = parseInt(this.qtyInput.min, 10) || 1;
        const max = this.qtyInput.max ? parseInt(this.qtyInput.max, 10) : null;
        const value = parseInt(this.qtyInput.value, 10) || min;
        const minus = this.querySelector('[data-ghan-qty-minus]');
        const plus = this.querySelector('[data-ghan-qty-plus]');

        if (minus) minus.disabled = value <= min;
        if (plus) plus.disabled = max !== null && value >= max;
      }

      get selectedOptions() {
        const values = [];
        this.inputs.forEach((input) => {
          if (input.checked) values[Number(input.dataset.optionIndex)] = input.value;
        });
        return values;
      }

      findVariant() {
        const selected = this.selectedOptions;
        if (!selected.length) return this.variants[0];
        return this.variants.find((variant) => variant.options.every((value, index) => value === selected[index]));
      }

      onOptionChange() {
        this.current = this.findVariant();
        this.render({ userChange: true });
      }

      render({ userChange }) {
        this.renderOptionAvailability();
        this.renderPrice();
        this.renderTrade();
        this.renderSku();
        this.renderSubmit();
        if (this.qtyInput) this.renderQuantityState();

        if (this.current && this.gallery) this.gallery.select(this.current.media_id);
        if (this.idInput) {
          this.idInput.value = this.current ? this.current.id : '';
          this.idInput.disabled = !this.current || !this.current.available;
        }

        /* Only on a real selection — pickup-availability fetches on its
           own when the page first loads. */
        if (!userChange) return;
        if (this.pickup && typeof this.pickup.update === 'function') this.pickup.update(this.current);
        if (this.current && this.dataset.updateUrl !== 'false') {
          const url = new URL(window.location.href);
          url.searchParams.set('variant', this.current.id);
          window.history.replaceState({}, '', url.toString());
        }
      }

      /* A value stays selectable but is struck through when no available
         variant combines it with the other currently-selected values. */
      renderOptionAvailability() {
        const selected = this.selectedOptions;

        this.inputs.forEach((input) => {
          const index = Number(input.dataset.optionIndex);
          const reachable = this.variants.some((variant) => {
            if (!variant.available) return false;
            if (variant.options[index] !== input.value) return false;
            return variant.options.every((value, i) => i === index || value === selected[i]);
          });

          const pill = input.closest('.ghan-pill');
          if (pill) pill.classList.toggle('ghan-pill--unavailable', !reachable);
        });
      }

      renderPrice() {
        if (!this.priceTarget) return;

        if (!this.current) {
          this.priceTarget.innerHTML = `<span class="gb-price">${this.dataset.unavailableText || 'Unavailable'}</span>`;
          return;
        }

        const variant = this.current;
        const onSale = variant.compare_at && variant.compare_at > variant.price;
        const headline = variant.unit_money
          ? `${variant.unit_money}<span class="gb-price__unit"> / ${variant.unit_ref}</span>`
          : variant.price_money;

        let html = `<span class="gb-price${onSale ? ' gb-price--sale' : ''}">${headline}</span>`;

        if (onSale) {
          html += `<s class="gb-price__was">${variant.compare_at_money}</s>`;
          const off = Math.round(((variant.compare_at - variant.price) / variant.compare_at) * 100);
          if (off > 0) html += `<span class="gb-badge gb-badge--sale">Save ${off}%</span>`;
        }
        if (!variant.available) {
          html += `<span class="gb-badge gb-badge--soldout">${this.dataset.soldOutText || 'Sold out'}</span>`;
        }

        this.priceTarget.innerHTML = html;
      }

      /* Wholesale trade price (Stage 06). PRESENTATION ONLY — Shopify's
         segment-gated automatic discount is what charges it. `trade_money`
         is null in the variant JSON for every visitor who is not a logged-in
         `wholesale` customer, so this is a no-op for everyone else. */
      renderTrade() {
        if (!this.tradeTarget) return;

        const value = this.tradeTarget.querySelector('.gb-trade__value');
        const variant = this.current;

        if (!value || !variant || !variant.trade_money) {
          this.tradeTarget.hidden = true;
          return;
        }

        this.tradeTarget.hidden = false;
        value.innerHTML =
          variant.unit_money && variant.unit_ref
            ? `${variant.trade_money}<span class="gb-price__unit"> / ${variant.unit_ref}</span>`
            : variant.trade_money;
      }

      renderSku() {
        if (!this.skuTarget) return;
        const sku = this.current && this.current.sku ? this.current.sku : '';
        this.skuTarget.textContent = sku;
        this.skuTarget.hidden = sku === '';
      }

      renderSubmit() {
        if (!this.submit || !this.submitText) return;

        if (!this.current) {
          this.submit.disabled = true;
          this.submitText.textContent = this.dataset.unavailableText || 'Unavailable';
          return;
        }
        if (!this.current.available) {
          this.submit.disabled = true;
          this.submitText.textContent = this.dataset.soldOutText || 'Sold out';
          return;
        }

        this.submit.disabled = false;
        this.submitText.textContent = this.showPriceInButton
          ? `${this.addLabel} — ${this.lineTotal()}`
          : this.addLabel;
      }

      /* Quantity 1 reuses the server-rendered money string so the default
         view matches the store's money format exactly. */
      lineTotal() {
        const quantity = this.qtyInput ? parseInt(this.qtyInput.value, 10) || 1 : 1;
        if (quantity === 1) return this.current.price_money;

        try {
          return new Intl.NumberFormat(this.locale, {
            style: 'currency',
            currency: this.currency,
          }).format((this.current.price * quantity) / 100);
        } catch (error) {
          return this.current.price_money;
        }
      }
    }
  );
}

if (!customElements.get('ghan-cutoff')) {
  customElements.define(
    'ghan-cutoff',
    class GhanCutoff extends HTMLElement {
      connectedCallback() {
        this.deadline = Number(this.dataset.deadline) * 1000;
        this.passed = this.dataset.passed === 'true';
        this.target = this.querySelector('[data-ghan-cutoff-text]');
        if (!this.target || !this.deadline) return;

        this.tick();
        this.timer = window.setInterval(() => this.tick(), 1000);
      }

      disconnectedCallback() {
        window.clearInterval(this.timer);
      }

      tick() {
        let remaining = this.deadline - Date.now();

        /* Cutoff rolled over while the page was open (or the HTML was
           edge-cached past it): move to the next day's cutoff. */
        if (remaining <= 0) {
          this.deadline += 86400000;
          this.passed = true;
          remaining = this.deadline - Date.now();
        }

        const template = this.passed ? this.dataset.templatePassed : this.dataset.template;
        if (!template) {
          this.hidden = true;
          return;
        }

        const next = template.replace('{time}', this.humanise(remaining));
        if (next !== this.lastRendered) {
          this.target.innerHTML = next;
          this.lastRendered = next;
        }
        this.classList.toggle('ghan-pdp__cutoff--passed', this.passed);
      }

      humanise(ms) {
        const totalMinutes = Math.ceil(ms / 60000);
        const hours = Math.floor(totalMinutes / 60);
        const minutes = totalMinutes % 60;
        if (hours > 0) return `${hours} h ${minutes} m`;
        return `${Math.max(minutes, 1)} m`;
      }
    }
  );
}
